import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { VerificationTrace } from '../../../../types/verificationTrace';
import { gray5, gray40, gray80, gray100, white } from '../../../../constants/colors';
import { PROXY_BANNER_HEIGHT } from '../../../../constants/labels';
import VerificationTableView from './VerificationTableView';

/**
 * 検証トレース表示パネル（#3109 運営スタッフ限定）
 *
 * 結果画面の情報欄の検証マークから開く。左の帳票画像に被らないよう
 * 右側から出る Drawer で、OCR結果 / プロンプト / 値取得結果をタブ切替で表示する。
 * 構造化の最終結果は結果画面に表示済みのため含めない。
 */

type VerificationTracePanelProps = {
  open: boolean;
  onClose: () => void;
  trace: VerificationTrace;
  // 代理モード中は上部に固定の代理バナー（高さ PROXY_BANNER_HEIGHT）があるため、
  // Drawer の上端をその分下げてバナーに隠れないようにする（#3109）。
  isProxyMode?: boolean;
};

// 表示タブの種別
const traceTab = {
  ocr: 0,
  prompt: 1,
  geminiResult: 2,
} as const;

type TraceTabValue = (typeof traceTab)[keyof typeof traceTab];

// ページ番号ラベル（0 は統合処理＝全ページ一括を表す）
const pageLabel = (pageNumber: number): string =>
  pageNumber === 0 ? '全ページ（統合処理）' : `ページ${pageNumber}`;

// パネル幅のデフォルト・最小（px）。最大は実行時に画面幅の半分で算出する。
const DEFAULT_PANEL_WIDTH = 480;
const MIN_PANEL_WIDTH = 360;

const VerificationTracePanel = ({
  open,
  onClose,
  trace,
  isProxyMode = false,
}: VerificationTracePanelProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<TraceTabValue>(traceTab.ocr);
  // OCR結果タブの表示モード（#3109）。'raw'=生テキスト / 'table'=表プレビュー。
  // 人間が表として確認しやすいよう、表プレビューをデフォルトにする
  // （表構造がない実行では showTablePreview 判定で自動的に生テキスト表示になる）。
  const [ocrViewMode, setOcrViewMode] = useState<'raw' | 'table'>('table');

  // パネル幅（#3109）。左端のハンドルをドラッグして最小〜画面半分の範囲で調整できる。
  const [panelWidth, setPanelWidth] = useState<number>(DEFAULT_PANEL_WIDTH);
  const isResizingRef = useRef<boolean>(false);

  // ドラッグ中の幅計算（右端 Drawer なので幅 = 画面右端 − マウスX）。
  // 範囲は MIN_PANEL_WIDTH 〜 画面幅の半分にクランプする。
  const handleResizeMove = useCallback((e: MouseEvent): void => {
    if (!isResizingRef.current) return;
    const maxWidth = window.innerWidth / 2;
    const next = window.innerWidth - e.clientX;
    setPanelWidth(Math.min(Math.max(next, MIN_PANEL_WIDTH), maxWidth));
  }, []);

  const stopResize = useCallback((): void => {
    isResizingRef.current = false;
    document.body.style.userSelect = '';
  }, []);

  const startResize = useCallback((e: React.MouseEvent): void => {
    e.preventDefault();
    isResizingRef.current = true;
    // ドラッグ中にテキストが選択されないようにする
    document.body.style.userSelect = 'none';
  }, []);

  // ドラッグ中のグローバルな mousemove/mouseup を購読する
  useEffect(() => {
    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', stopResize);
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', stopResize);
    };
  }, [handleResizeMove, stopResize]);

  // 表構造（table_text）を持つページがあるか。OCR結果タブでのみトグルを出す判定に使う。
  const hasTableText = trace.pages.some(page => page.table_text);
  // 表プレビューを表示するか（OCR結果タブ かつ 表構造あり かつ モードがtable）
  const showTablePreview = activeTab === traceTab.ocr && hasTableText && ocrViewMode === 'table';

  // 選択中タブに対応するテキストを、全ページ分を見出し付きで連結して返す
  const buildTabText = (tab: TraceTabValue): string =>
    trace.pages
      .map(page => {
        const heading = `===== ${pageLabel(page.page_number)} =====`;
        let body = '';
        if (tab === traceTab.ocr) {
          // OCR結果：行テキスト。表構造（DI）があれば併記する
          body = page.table_text
            ? `${page.ocr_text}\n\n--- 表構造（Document Intelligence）---\n${page.table_text}`
            : page.ocr_text;
        } else if (tab === traceTab.prompt) {
          body = page.gemini_prompt;
        } else {
          body = page.gemini_ocr_result;
        }
        return `${heading}\n${body}`;
      })
      .join('\n\n');

  const currentText = buildTabText(activeTab);

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(currentText);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // 背後の帳票画像を操作（拡大など）できるよう、オーバーレイ（グレーの幕）を出さず、
      // 背後クリックを通す非モーダル表示にする。
      //   - hideBackdrop: グレーの幕を消す
      //   - disableEnforceFocus/disableScrollLock: 背後の操作・スクロールを妨げない
      //   - root（Modal）は pointer-events: none にして背後へクリックを通し、
      //     パネル本体（Paper）だけ pointer-events: auto に戻す
      hideBackdrop
      disableEnforceFocus
      disableScrollLock
      ModalProps={{ sx: { pointerEvents: 'none' } }}
      // 帳票画像（左領域）に被らないよう右側に固定幅で表示する。
      // 代理モード中は上部の代理バナー（zIndex 1400・高さ PROXY_BANNER_HEIGHT）に
      // 隠れないよう、Drawer の上端をバナーの高さ分だけ下げる。
      PaperProps={{
        sx: {
          // 幅はドラッグで可変（panelWidth）。狭い画面でも収まるよう 90vw を上限にする。
          width: `${panelWidth}px`,
          maxWidth: '90vw',
          backgroundColor: white,
          pointerEvents: 'auto',
          overflow: 'visible', // 左端のリサイズハンドルがはみ出せるように
          // 左枠にうっすらグレーの境界線を入れる
          borderLeft: `1px solid ${gray40}`,
          // 幕がない分、境界が分かるよう影を付ける
          boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.15)',
          ...(isProxyMode
            ? {
                top: `${PROXY_BANNER_HEIGHT}px`,
                height: `calc(100% - ${PROXY_BANNER_HEIGHT}px)`,
              }
            : {}),
        },
      }}
    >
      {/* リサイズハンドル（#3109）。左端をドラッグしてパネル幅を最小〜画面半分で調整する。
          縦線は出さず、上下中央のつまみ（グリップ）だけでリサイズを示す。 */}
      <Box
        onMouseDown={startResize}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '8px',
          height: '100%',
          cursor: 'col-resize',
          zIndex: 1,
        }}
      >
        {/* 上下中央のつまみ（グリップ）。左枠線の上に重ねて配置し、ホバーで少し濃くする */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '-3px', // 左枠線（Paper の borderLeft）の上に重ねる
            transform: 'translateY(-50%)',
            width: '6px',
            height: '64px',
            borderRadius: '3px',
            backgroundColor: gray40,
            transition: 'background-color 0.15s',
            '&:hover': {
              backgroundColor: gray80,
            },
          }}
        />
      </Box>
      <Stack sx={{ height: '100%' }}>
        {/* ヘッダー */}
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: `1px solid ${gray5}`,
          }}
        >
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: gray80 }}>
            検証ログ
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: gray40 }}>
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Stack>

        {/* タブ */}
        <Tabs
          value={activeTab}
          onChange={(_, value: TraceTabValue): void => setActiveTab(value)}
          variant="fullWidth"
          // gendrive の他モーダル（マスタ照合・辞書照合）のタブと同じ配色に統一する。
          // 選択中タブ・下線は黒(gray100)、非選択はグレー(gray80)。MUI デフォルトの青は使わない。
          sx={{
            borderBottom: `1px solid ${gray5}`,
            minHeight: '40px',
            '& .MuiTabs-indicator': { backgroundColor: gray100 },
            '& .MuiTab-root.Mui-selected': { color: gray100 },
            '& .MuiTab-root': { color: gray80 },
          }}
        >
          <Tab label="OCR結果" value={traceTab.ocr} sx={{ minHeight: '40px', fontSize: '13px' }} />
          <Tab
            label="値取得プロンプト"
            value={traceTab.prompt}
            sx={{ minHeight: '40px', fontSize: '13px' }}
          />
          <Tab
            label="値取得結果"
            value={traceTab.geminiResult}
            sx={{ minHeight: '40px', fontSize: '13px' }}
          />
        </Tabs>

        {/* 操作行：OCR結果タブで表構造があるときは生テキスト/表プレビューのトグルを出す。右端はコピー。 */}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 12px',
          }}
        >
          {activeTab === traceTab.ocr && hasTableText ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={ocrViewMode}
              onChange={(_, value: 'raw' | 'table' | null): void => {
                if (value) setOcrViewMode(value);
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: '11px',
                  padding: '2px 10px',
                  color: gray80,
                  textTransform: 'none',
                },
                '& .Mui-selected': {
                  color: gray100,
                  backgroundColor: gray5,
                },
              }}
            >
              <ToggleButton value="raw">生テキスト</ToggleButton>
              <ToggleButton value="table">表プレビュー</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Box />
          )}
          <Tooltip title="クリップボードにコピー">
            <IconButton size="small" onClick={handleCopy} sx={{ color: gray40 }}>
              <ContentCopyIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* 本文（スクロール）。表プレビュー時は各ページの表構造を表として描画、
            それ以外は等幅の生テキストを表示する。 */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            padding: '0 16px 16px',
          }}
        >
          {showTablePreview ? (
            trace.pages.map((page, index) => (
              <Box key={index} sx={{ marginBottom: '16px' }}>
                <Typography
                  sx={{ fontSize: '12px', fontWeight: 700, color: gray80, marginBottom: '4px' }}
                >
                  {pageLabel(page.page_number)}
                </Typography>
                {/* 行テキスト（OCRが拾った生の行）は等幅で、表構造は表として描画 */}
                <Box
                  component="pre"
                  sx={{
                    margin: '0 0 8px',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '12px',
                    lineHeight: 1.6,
                    color: gray80,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {page.ocr_text}
                </Box>
                {page.table_text && <VerificationTableView html={page.table_text} />}
              </Box>
            ))
          ) : (
            <Box
              component="pre"
              sx={{
                margin: 0,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: '12px',
                lineHeight: 1.6,
                color: gray80,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {currentText || '（データがありません）'}
            </Box>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
};

export default VerificationTracePanel;
